/**
 * The gift success component that gets used at the end of /gift flows
 * and also if /send flows chose to send a gift
 */
import PropTypes from 'prop-types'

import React from 'react'
import { connect } from 'transfer/utils/p2p-connect'
import SuccessBase from './success-base'
import * as ViewActions from '../actions/Actions'
import { withRouter, Link } from 'react-router-dom' // see note below for why this is included
import _ from 'lodash'
import SendPPMeClaimIframe from './send-ppme-claim-iframe'
import RTRSuccessBreakdown from './rtrSuccessBreakdown'
import { getGoToLink, getGoToLinkContentKey } from '../utils/success-utils'
import enRTRStrings from '../../../../config/enRTRStrings'
import LINKS from '../../../../config/links.json'
import content from 'pp-react-l10n'
let i18n = content('transfer/send/success')

class GiftSuccess extends React.Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    amountFormatted: PropTypes.string.isRequired,
    recipient: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    userState: PropTypes.string.isRequired,
    ppmeClaimUrl: PropTypes.string.isRequired,
    showPPMeIframe: PropTypes.bool.isRequired,
    complianceInfo: PropTypes.shape({
      status: PropTypes.string,
    }),
    selectedFundingOption: PropTypes.shape({
      funding_sources: PropTypes.array.isRequired,
      isRTR: PropTypes.bool.isRequired,
    }),
    isPurchase: PropTypes.bool.isRequired,
    unified: PropTypes.bool.isRequired,
    themeId: PropTypes.string,
    isBusinessUser: PropTypes.bool.isRequired,
    language: PropTypes.string,
  }

  componentDidMount() {
    // trigger the external hook for the success page
    this.props.dispatch(
      ViewActions.triggerExternalHook('successPageAfterRender', this.props)
    )
  }

  componentWillUnmount() {
    this.props.dispatch(ViewActions.leavingSuccessPage())
  }

  render() {
    const {
      selectedFundingOption: { funding_sources, isRTR = false },
      complianceInfo: { status = '' } = {},
      language = '',
    } = this.props
    const isOFAC = status === 'PENDING'
    const shouldRenderAvoidFee =
      !this.props.isPurchase &&
      _.some(funding_sources, { paypal_instrument_type: 'CARD' })
    const showENSubTextRTR = isRTR === true && language !== 'en'
    const recipient = this.props.displayName || this.props.recipient

    const successHeaderContent = i18n('gift.successHeader', {
      recipient,
      amountFormatted: this.props.amountFormatted,
    })

    const successHeaderTranslatedContent = `${enRTRStrings.giftSuccessHeader[0]} ${this.props.amountFormatted} ${enRTRStrings.giftSuccessHeader[1]} ${recipient}`

    const rtrSuccessBreakdownComponent = (
      <RTRSuccessBreakdown
        selectedFundingOption={this.props.selectedFundingOption}
        complianceInfo={this.props.complianceInfo}
        isGoods={this.props.isPurchase}
        recipient={this.props.recipient}
        showENSubTextRTR={showENSubTextRTR}
      />
    )

    const sendMoreLink = this.props.unified
      ? '/myaccount/transfer/homepage'
      : '/myaccount/transfer/gift'

    const doneLinksComponent = (
      <div className="doneLinks">
        <Link
          to={sendMoreLink}
          className="vx_btn done"
          data-pagename="main:consumer:p2p:transfer:gift:start"
          data-pagename2="main:consumer:p2p:transfer:gift:start:::"
        >
          {i18n('gift.sendMore')}
        </Link>

        {shouldRenderAvoidFee && (
          <p>
            {i18n.html('success.avoidFee', {
              addBankUrl: LINKS.ALL.ADD_BANK_URL,
            })}
          </p>
        )}

        {!this.props.isBusinessUser && (
          <div className="action">
            <a href={getGoToLink()}>{i18n(getGoToLinkContentKey())}</a>
          </div>
        )}
      </div>
    )

    return (
      <SuccessBase
        amountFormatted={this.props.amountFormatted}
        recipient={this.props.recipient}
        displayName={this.props.displayName}
        userState={this.props.userState}
        ppmeClaimUrl={this.props.ppmeClaimUrl}
        showPPMeIframe={this.props.showPPMeIframe}
        selectedFundingOption={this.props.selectedFundingOption}
        complianceInfo={this.props.complianceInfo}
        pageName={'main:consumer:p2p:transfer:gift:start'}
        pageName2={'main:consumer:p2p:transfer:gift:start:::'}
        isRTR={isRTR}
        isOFAC={isOFAC}
        ppmeClaimIframeComponent={<SendPPMeClaimIframe />}
        rtrSuccessBreakdownComponent={rtrSuccessBreakdownComponent}
        successHeaderContent={successHeaderContent}
        successHeaderTranslatedContent={successHeaderTranslatedContent}
        showThemeBadge={true}
        showGiftBlurb={true}
        themeId={this.props.themeId}
        doneLinksComponent={doneLinksComponent}
        showAppBanner={true}
        showENSubTextRTR={showENSubTextRTR}
      />
    )
  }
}

// We wrap with withRouter here for the cases when this component is registered directly on the router (i.e. /gift/success)
export default withRouter(
  connect(
    ({ address, amount, recipient, transaction, theme, server, user }) => ({
      amountFormatted: amount.amountFormatted,
      recipient: recipient.recipient,
      displayName: recipient.displayName,
      userState:
        address.addresses &&
        address.addresses[0] &&
        address.addresses[0].state &&
        address.addresses[0].state.value,
      isPurchase: transaction.isPurchase,
      ppmeClaimUrl: transaction.ppmeClaimUrl,
      showPPMeIframe: transaction.showPPMeIframe,
      selectedFundingOption: transaction.selectedFundingOption,
      complianceInfo: transaction.complianceInfo,
      themeId: theme.themeId,
      isBusinessUser: server.businessAccountInfo.isBusinessUser,
      language: user.language,
    })
  )(GiftSuccess)
)
